import { FunctionComponent } from 'react'
import { WHelper, WParagraph, WTitle } from '@alice-health/wonderland-react'
import { SpecialistRatingData } from '@/modules/accredited-establishments/api'

interface RatingCard {
  label: string
  value: number
  suffix?: string
}

interface SpecialistRatingProps {
  rating: SpecialistRatingData
}

/*
 * O backend manda os scores como fracao (0.98), e a tela mostra porcentagem
 * inteira (98%).
 */
const toPercentage = (score: number): number => Math.round(score * 100)

/*
 * O backend so devolve `rating` com 10 ou mais avaliacoes, e nunca para rede
 * Cassi nem comunidade clinica. Quando ele omite, este componente nao e
 * montado e o drawer segue sem espaco vazio (HC-2644).
 */
const getCards = ({
  trustScore,
  recommendationScore,
  appointmentCount
}: SpecialistRatingData): RatingCard[] =>
  [
    trustScore === undefined
      ? undefined
      : { label: 'Confiam', value: toPercentage(trustScore), suffix: '%' },
    recommendationScore === undefined
      ? undefined
      : {
          label: 'Indicariam',
          value: toPercentage(recommendationScore),
          suffix: '%'
        },
    { label: 'Consultas', value: appointmentCount }
  ].filter((card): card is RatingCard => Boolean(card))

const SpecialistRating: FunctionComponent<SpecialistRatingProps> = ({
  rating
}) => {
  const { aiGeneratedComment, evaluationsCount } = rating

  const cards = getCards(rating)

  return (
    <div
      data-testid='specialist-rating'
      className='flex flex-col gap-gl-spacing-08 px-gl-spacing-06 lg:px-0'
    >
      {aiGeneratedComment ? (
        <div className='flex flex-col gap-gl-spacing-04'>
          <WParagraph mode='secondary'>{aiGeneratedComment}</WParagraph>

          <WHelper mode='tertiary'>
            {`Opinião de ${evaluationsCount} membros consolidada por Inteligência Artificial`}
          </WHelper>
        </div>
      ) : null}

      {cards.length ? (
        <div className='grid grid-cols-3 gap-gl-spacing-01'>
          {cards.map(({ label, value, suffix }) => (
            <div
              key={label}
              data-testid='specialist-rating-card'
              /* Padding assimetrico do handoff (node 26:40608): 16 / 16 / 12 / 20. */
              className='flex flex-col gap-gl-spacing-10 rounded-gl-border-radius-lg border border-gl-color-transparent-black-10 pb-gl-spacing-03 pl-gl-spacing-05 pr-gl-spacing-04 pt-gl-spacing-04'
            >
              <WHelper>{label}</WHelper>

              <div className='flex items-baseline'>
                <WTitle
                  size='large'
                  mode='active'
                >
                  {value}
                </WTitle>

                {suffix ? (
                  <WTitle
                    size='small'
                    mode='active'
                  >
                    {suffix}
                  </WTitle>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export type { SpecialistRatingProps }

export { SpecialistRating }
